import React, { useState, useEffect, useCallback } from "react";
import { getTaches, getProfils, createTache, updateTache, getTache, setTacheProfils } from "../services/api";
import ModaleV2 from "../components/ModaleV2";
import IconeV2 from "../components/IconeV2";

// ===================================================================
// The Crowd v2 — Pilotage › Taches
//
// Adaptation v2 de pages/GestionTaches.js : memes appels API, meme regle
// de lecture seule (seul un directeur modifie, comme dans App.js).
//
// Changements d'affichage, alignes sur Equipes : filtre par categorie
// en bascule, formulaire dans une modale, et deux vues au choix —
// cartes (defaut, regroupees par categorie quand le filtre est
// « Toutes ») ou liste. Le choix de vue est memorise dans le navigateur.
//
// Refonte du module Projets : la page ne gere plus que les taches
// Recurrent et Specifique, proposees a la saisie selon le profil. Les
// etapes se gerent depuis leur projet (fiche projet, Pilotage › Projets) ;
// les taches « Projet » sont desactivees et remplacees par les etapes.
// Le backend refuse d'ailleurs ces deux categories (taches.py).
// ===================================================================

const CATEGORIES = ["Recurrent", "Specifique"];
const LIBELLES_CATEGORIES = { Recurrent: "Récurrent", Specifique: "Spécifique" };

const FORM_VIDE = { nom: "", categorie: "Recurrent", profil_ids: [] };

const VUE_STORAGE_KEY = "thecrowd-v2-vue-taches";

function vueInitiale() {
  try {
    const v = window.localStorage.getItem(VUE_STORAGE_KEY);
    if (v === "cartes" || v === "liste") return v;
  } catch {
    // stockage indisponible : vue par defaut
  }
  return "cartes";
}

function BadgeCategorie({ categorie }) {
  return (
    <span className="v2-statut v2-statut-neutre">
      {LIBELLES_CATEGORIES[categorie] || categorie}
    </span>
  );
}

function BoutonsTache({ t, compact, onModifier, onBasculer }) {
  const taille = compact ? " v2-btn-compact" : "";
  return (
    <>
      <button type="button" className={`v2-btn v2-btn-secondaire${taille}`} onClick={onModifier}>
        <IconeV2 nom="crayon" /> Modifier
      </button>
      <button type="button" className={`v2-btn v2-btn-secondaire${taille}${t.active ? " v2-btn-danger-texte" : ""}`} onClick={onBasculer}>
        {t.active ? "Désactiver" : "Réactiver"}
      </button>
    </>
  );
}

// --- Vue cartes ---

function CarteTache({ t, readOnly, onModifier, onBasculer }) {
  return (
    <article className={`v2-tache${t.active ? "" : " inactif"}`}>
      <div className="v2-tache-corps">
        <div className="v2-tache-entete">
          <h3 className="v2-tache-nom">{t.nom}</h3>
          {!t.active && <span className="v2-statut v2-statut-neutre">Inactive</span>}
        </div>
        <div className="v2-tache-meta">
          <BadgeCategorie categorie={t.categorie} />
        </div>
      </div>
      {!readOnly && (
        <div className="v2-collab-actions">
          <BoutonsTache t={t} onModifier={onModifier} onBasculer={onBasculer} />
        </div>
      )}
    </article>
  );
}

function GrilleTaches({ taches, readOnly, onModifier, onBasculer }) {
  return (
    <div className="v2-tache-grille">
      {taches.map((t) => (
        <CarteTache key={t.id} t={t} readOnly={readOnly} onModifier={() => onModifier(t)} onBasculer={() => onBasculer(t)} />
      ))}
    </div>
  );
}

// Regroupement par categorie, dans l'ordre du catalogue ; categories vides omises
function VueCartes({ taches, groupee, readOnly, onModifier, onBasculer }) {
  if (taches.length === 0) return <div className="v2-panneau"><p className="v2-vide">Aucune tâche.</p></div>;
  if (!groupee) return <GrilleTaches taches={taches} readOnly={readOnly} onModifier={onModifier} onBasculer={onBasculer} />;
  const groupes = CATEGORIES
    .map((c) => ({ cle: c, titre: LIBELLES_CATEGORIES[c], liste: taches.filter((t) => t.categorie === c) }))
    .filter((g) => g.liste.length > 0);
  return (
    <>
      {groupes.map((g) => (
        <section key={g.cle} className="v2-tache-section" aria-labelledby={`v2-taches-${g.cle}`}>
          <div className="v2-section-titre">
            <h2 id={`v2-taches-${g.cle}`}>{g.titre}</h2>
            <span className="v2-section-filet" />
            <span className="v2-cellule-discrete">{g.liste.length}</span>
          </div>
          <GrilleTaches taches={g.liste} readOnly={readOnly} onModifier={onModifier} onBasculer={onBasculer} />
        </section>
      ))}
    </>
  );
}

// --- Vue liste ---

function VueListe({ taches, readOnly, onModifier, onBasculer }) {
  return (
    <div className="v2-panneau v2-tableau-conteneur">
      <table className="v2-tableau v2-taches">
        <thead>
          <tr>
            <th scope="col">Nom</th>
            <th scope="col">Catégorie</th>
            <th scope="col">Statut</th>
            {!readOnly && <th scope="col" className="v2-taches-col-actions">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {taches.length === 0 && (
            <tr><td colSpan={readOnly ? 3 : 4} className="v2-vide">Aucune tâche.</td></tr>
          )}
          {taches.map((t) => (
            <tr key={t.id} className={t.active ? "" : "v2-ligne-inactive"}>
              <td className="v2-gras">{t.nom}</td>
              <td><BadgeCategorie categorie={t.categorie} /></td>
              <td>
                <span className={`v2-statut ${t.active ? "v2-statut-success" : "v2-statut-neutre"}`}>
                  {t.active ? "Active" : "Inactive"}
                </span>
              </td>
              {!readOnly && (
                <td className="v2-taches-col-actions">
                  <span className="v2-taches-actions">
                    <BoutonsTache t={t} compact onModifier={() => onModifier(t)} onBasculer={() => onBasculer(t)} />
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Modale formulaire ---

function ModaleTache({ edition, form, setForm, profils, onValider, onClose }) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const basculerProfil = (id) =>
    setForm((prev) => ({
      ...prev,
      profil_ids: prev.profil_ids.includes(id) ? prev.profil_ids.filter((x) => x !== id) : [...prev.profil_ids, id],
    }));

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider();
    } catch (err) {
      setErreur(err.message || "Erreur inattendue.");
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre={edition ? "Modifier la tâche" : "Nouvelle tâche"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours}>
            {enCours ? "Enregistrement…" : edition ? "Enregistrer les modifications" : "Enregistrer"}
          </button>
        </>
      }
    >
      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-tache-nom">Nom de la tâche</label>
        <input id="v2-tache-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} autoFocus />
      </div>

      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-tache-categorie">Catégorie</label>
        <select id="v2-tache-categorie" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{LIBELLES_CATEGORIES[c]}</option>)}
        </select>
        <span className="v2-champ-aide">Les étapes de projet se créent depuis la fiche du projet.</span>
      </div>

      <fieldset className="v2-groupe-champs">
        <legend className="v2-label">Profils associés</legend>
        <div className="v2-cases">
          {profils.map((p) => (
            <label key={p.id} className="v2-case-simple">
              <input type="checkbox" checked={form.profil_ids.includes(p.id)} onChange={() => basculerProfil(p.id)} />
              <span>{p.nom}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
    </ModaleV2>
  );
}

// --- Page ---

function GestionTachesV2({ collaborateur }) {
  const readOnly = collaborateur?.role !== "directeur";
  const [taches, setTaches] = useState([]);
  const [profils, setProfils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [filterCat, setFilterCat] = useState(null);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(FORM_VIDE);
  const [message, setMessage] = useState(null); // { type, texte }
  const [vue, setVue] = useState(vueInitiale);

  useEffect(() => {
    try {
      window.localStorage.setItem(VUE_STORAGE_KEY, vue);
    } catch {
      // cf. vueInitiale
    }
  }, [vue]);

  const loadData = useCallback(() => {
    setErreurChargement(null);
    getTaches(filterCat)
      // Les categories de projet ne sont plus gerees ici (historique desactive)
      .then((liste) => setTaches(liste.filter((t) => CATEGORIES.includes(t.categorie))))
      .catch((err) => setErreurChargement(`Tâches non chargées : ${err.message}`))
      .finally(() => setLoading(false));
    getProfils().then(setProfils).catch(() => {});
  }, [filterCat]);

  useEffect(() => { loadData(); }, [loadData]);

  const ouvrirCreation = () => {
    setEditingId(null);
    setForm(FORM_VIDE);
    setMessage(null);
    setModaleOuverte(true);
  };

  const ouvrirEdition = async (tache) => {
    if (readOnly) return;
    setMessage(null);
    try {
      const detail = await getTache(tache.id);
      setEditingId(tache.id);
      setForm({
        nom: detail.nom,
        categorie: detail.categorie,
        profil_ids: detail.profils ? detail.profils.map((p) => p.id) : [],
      });
      setModaleOuverte(true);
    } catch (err) {
      setMessage({ type: "erreur", texte: `Erreur : ${err.message}` });
    }
  };

  const fermerModale = () => {
    setModaleOuverte(false);
    setEditingId(null);
    setForm(FORM_VIDE);
  };

  // Levent en cas d'erreur : la modale affiche le message et reste ouverte
  const enregistrer = async () => {
    if (readOnly) return;
    const payload = { nom: form.nom, categorie: form.categorie };

    if (editingId) {
      await updateTache(editingId, payload);
      await setTacheProfils(editingId, form.profil_ids.map(Number));
      setMessage({ type: "succes", texte: "Tâche mise à jour." });
    } else {
      payload.profil_ids = form.profil_ids.map(Number);
      await createTache(payload);
      setMessage({ type: "succes", texte: "Tâche ajoutée." });
    }
    fermerModale();
    loadData();
  };

  const basculerActive = async (tache) => {
    if (readOnly) return;
    setMessage(null);
    try {
      await updateTache(tache.id, { active: !tache.active });
      loadData();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Erreur : ${err.message}` });
    }
  };

  return (
    <div className="v2-page v2-page-module">
      <div className="v2-bib-entete">
        <header className="v2-module-entete">
          <h1 className="v2-module-titre">Tâches</h1>
          <p className="v2-sous-titre">
            Catalogue des tâches proposées à la saisie des temps selon le profil. Les étapes de projet se gèrent depuis leur projet.
            {readOnly && " Lecture seule : seul un directeur peut modifier."}
          </p>
        </header>
        {!readOnly && (
          <button type="button" className="v2-btn v2-btn-principal" onClick={ouvrirCreation}>
            <IconeV2 nom="plus" /> Ajouter une tâche
          </button>
        )}
      </div>

      <div className="v2-barre-outils">
        <div className="v2-bascule" role="group" aria-label="Catégorie">
          <button type="button" className={`v2-bascule-choix${!filterCat ? " actif" : ""}`} aria-pressed={!filterCat} onClick={() => setFilterCat(null)}>
            Toutes
          </button>
          {CATEGORIES.map((c) => (
            <button key={c} type="button" className={`v2-bascule-choix${filterCat === c ? " actif" : ""}`} aria-pressed={filterCat === c} onClick={() => setFilterCat(c)}>
              {LIBELLES_CATEGORIES[c]}
            </button>
          ))}
        </div>
        <div className="v2-barre-outils-droite">
          <span className="v2-total-semaine">{taches.length} tâche{taches.length > 1 ? "s" : ""}</span>
          <div className="v2-bascule" role="group" aria-label="Affichage">
            <button type="button" className={`v2-bascule-choix${vue === "cartes" ? " actif" : ""}`} aria-pressed={vue === "cartes"} onClick={() => setVue("cartes")}>
              <IconeV2 nom="tableau" taille={13} /> Cartes
            </button>
            <button type="button" className={`v2-bascule-choix${vue === "liste" ? " actif" : ""}`} aria-pressed={vue === "liste"} onClick={() => setVue("liste")}>
              <IconeV2 nom="liste" taille={13} /> Liste
            </button>
          </div>
        </div>
      </div>

      {erreurChargement && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreurChargement}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      {loading ? (
        <div className="v2-chargement">Chargement…</div>
      ) : vue === "cartes" ? (
        <VueCartes taches={taches} groupee={!filterCat} readOnly={readOnly} onModifier={ouvrirEdition} onBasculer={basculerActive} />
      ) : (
        <VueListe taches={taches} readOnly={readOnly} onModifier={ouvrirEdition} onBasculer={basculerActive} />
      )}

      {modaleOuverte && !readOnly && (
        <ModaleTache
          edition={!!editingId}
          form={form}
          setForm={setForm}
          profils={profils}
          onValider={enregistrer}
          onClose={fermerModale}
        />
      )}
    </div>
  );
}

export default GestionTachesV2;
